import { useEffect, useState } from "react";

import { Box, Button, Divider, FormControl, FormErrorMessage, FormLabel, HStack, Icon, Input, Textarea, Text, useDisclosure, useToast } from "@chakra-ui/react"
import { Field, FieldProps, Form, Formik } from "formik"
import { useLocation, useNavigate } from "react-router-dom"
import { HttpStatusCode } from "axios";
import { FaEye } from "react-icons/fa";
import { format } from "date-fns";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import ArrowIcon from "@/assets/ArrowIcon";
import AddOthersIcon from "@/assets/AddOthersIcon";
import RemoveOthersIcon from "@/assets/RemoveOthersIcon";
import EditIcon from "@/assets/EditIcon";
import FileUploader from "@/components/Fields/FileUploader";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import CurrencyInput from "@/components/Fields/CurrencyInput";
import SelectCountry from "@/components/Selects/SelectCountry";
import SelectShip from "@/components/Selects/SelectShip";
import SelectCurrency from "@/components/Selects/SelectCurrency";
import { COLORS, DURATION_TOAST, FORMAT_ADD_DATE } from "@/common/constants";
import currency from "@/data/currency";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { invoiceInspectorSchema } from "@/schemas/invoiceInspectorSchema";
import { postInvoiceInspector } from "@/services/invoicesInspectorService";
import { getDataVessel } from "@/services/vesselService";
import { getNominationById } from "@/services/nominationService";
import { downloadFile } from "@/services/downloadService";
import { parseCurrencyFloat } from "@/utils/currency";

import './customDatepicker.css';
import { array10of0s, array1to10, otherArray, otherCostArray, otherEvidenceFileIdArray, setByIndex } from "@/utils/arrayState";
import { Option } from "@/utils/select";
import EditFile from "@/components/Fields/EditFile";

const AddInvoicesInspectorProcess = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const inspectorId = useAppSelector((state: RootState) => state.account.appId);
    const inspectorName = useAppSelector((state: RootState) => state?.account?.companyName);

    const { id, sequenceId } = location.state || {};

    const [awaitingDays, setAwaitingDays] = useState(0);
    const [awaitingCost, setAwaitingCost] = useState(0);
    const [costInspection, setCostInspection] = useState(0);
    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");
    const [dataAdd, setDataAdd] = useState<any>(null);
    const [isDraft, setIsDraft] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [listShipName, setListShipName] = useState<any[]>([]);
	const [prefix, setPrefix] = useState('IDR');
    const [total, setTotal] = useState(0);
    const [totalArray, setTotalArray] = useState<number[]>(array10of0s());
    const [uploadedFiles, setUploadedFiles] = useState<File[]>();
    const [visibleOthers, setVisibleOthers] = useState([1]);
    const [title, setTitle] = useState("");
    const [defaultCurrency, setDefaultCurrency] = useState<any>(null);
    const [dataDetail, setDataDetail] = useState<any>(null);
    const [defaultShipName, setDefaultShipName] = useState<any>(null);
    const [editableDocs, setEditableDocs] = useState<string[]>([]);

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel to Create Invoice?");
        setConfirmationTest("Are you sure you want to cancel?");
        setConfirmationTitleButton("Yes");
    }

    const handleSubmit = (data: any) => {
        if (isDraft) {
            setTitle("Save Invoice?");
            setConfirmationTest("Are you sure you want to save invoice as draft?");
            setConfirmationTitleButton("Save Invoice");
        } else {
            setTitle("Create Invoice?");
            setConfirmationTest("Are you sure you want to create?");
            setConfirmationTitleButton("Create Invoice");
        }

        onOpen();
        setDataAdd(data);
        setIsSubmitting(true);
    }

    const generateDataAdd = () => {
        const data = new FormData();
        const vesselData = listShipName.find((x: any) => x.value === dataAdd.shipName);

        const others: {desc: string, cost: string, file: File}[] = [];
        array1to10.map((num) => {
            const shouldExist = Boolean(dataAdd[`other${num}`] || dataAdd[`otherCost${num}`]);
            if (shouldExist){
                others.push({
                    desc: dataAdd[`other${num}`] ?? '',
                    cost: dataAdd[`otherCost${num}`] ? parseCurrencyFloat(dataAdd[`otherCost${num}`]).toString() : '',
                    file: dataAdd[`otherCostFile${num}`] ?? '',
                })
            }
        });

        data.append('inspectorId', inspectorId);
        data.append('sequenceID', sequenceId || '');
        data.append('sireType', 'inspection');
        data.append('inspectorName', inspectorName);
        data.append('vesselId', vesselData?.value || '');
        data.append('invoiceNumber', dataAdd.invoiceNumber);
        data.append('shipName', vesselData?.label || '');
        data.append('invoiceDate', dataAdd.invoiceDate ? format(dataAdd.invoiceDate, FORMAT_ADD_DATE) : '');
        data.append('inspectionDate', dataAdd.inspectionDate ? format(dataAdd.inspectionDate, FORMAT_ADD_DATE) : '');
        data.append('country', dataAdd.country);
        data.append('port', dataAdd.port);
        data.append('jobDescription', dataAdd.jobDescription);
        data.append('costInspection', dataAdd.costInspection ? parseCurrencyFloat(dataAdd.costInspection).toString() : '');
        data.append('awaitingDays', dataAdd.awaitingDays ? parseCurrencyFloat(dataAdd.awaitingDays).toString() : '');
        data.append('awaitingDaysCost', dataAdd.awaitingCost ? parseCurrencyFloat(dataAdd.awaitingCost).toString() : '');
        array1to10.map((num, index) => {
            const obj = others[index];
            if (obj){
                data.append(`Other${num}`, obj.desc ?? '');
                data.append(`OtherCost${num}`, obj.cost ?? '');
                data.append(`OtherEvidence${num}File`, obj.file ?? '');
            } else {
                data.append(`Other${num}`, '');
                data.append(`OtherCost${num}`, '');
                data.append(`OtherEvidence${num}File`, '');
            }
        });
        data.append('subTotalCost', total.toString());
        data.append('totalAmount', total.toString());
        data.append('currency', currency.find((x: Option) => x.value === dataAdd.currency)?.label ?? '');
        if (typeof(uploadedFiles) === 'object' && uploadedFiles?.length > 0) {
            for (let i = 0; i < uploadedFiles?.length; i++) {
                data.append('files', uploadedFiles[i]);
            }
        } else {
            data.append('files', '');
        }
        data.append('InspectionResultfile', dataAdd.inspectionResult);
        data.append('InspectionResultId', dataDetail?.inspectionProcessResultId || '');
        data.append('invoiceStatus', isDraft ? '0' : '2');
        data.append('paymentStatus', '0');
        data.append('note', dataAdd.note);
        // for (let [key, value] of data.entries()) {
        //     console.log(`${key}: ${value}`);
        // }
        return data;
    }

    const handleConfirm = async () => {
        onClose();

        if (title === "Cancel to Create Invoice?") {
            //navigate('/inspection-process');
            history.back();
            return;
        }

        const obj = generateDataAdd();
        const response = await postInvoiceInspector(obj) as any;
        setIsSubmitting(false);
        if (response?.status === HttpStatusCode.Ok) {
            toast({
                title: 'Invoice created.',
                description: "We've created invoice for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                //navigate(role === 'super admin' ? '/manage-invoices/inspecting-company' : '/manage-invoices-inspecting-company');
                navigate('/manage-invoices/inspecting-company');
                // don't use history.back() here because this should redirect to a different page on success
            }, 1000)
        } else {
            toast({
                title: 'Invoice failed to create.',
                description: "We unabled to create invoice for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleDrop = (acceptedFiles: File[], fieldName: string, setFieldValue: any) => {
        setUploadedFiles(acceptedFiles);
        setFieldValue(fieldName, acceptedFiles);
    };

    const handleAddOthers = (index: number, isSubmitting: boolean) => {
        if (isSubmitting) {
            return;
        }

        const isExist = visibleOthers.includes(index + 1);
        if (!isExist) {
            setVisibleOthers([...visibleOthers, index + 1]);
        }
    }

    const handleRemoveOthers = (index: number, isSubmitting: boolean) => {
        if (isSubmitting) {
            return;
        }

        const isExist = visibleOthers.includes(index);
        if (isExist) {
            const newArr = visibleOthers.filter((x: number) => x !== index);
            setVisibleOthers(newArr);
            setTotalArray(setByIndex(totalArray, 0, index-1))
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleChangeCostInspection = (value: number, setFieldValue: any) => {
        setFieldValue(`costInspection`, value);
        setCostInspection(value);
    }

    const handleChangeAwaitingDays = (value: number, setFieldValue: any) => {
        setFieldValue(`awaitingDays`, value);
        setAwaitingDays(value);
    }

    const handleChangeAwaitingCost = (value: number, setFieldValue: any) => {
        setFieldValue(`awaitingCost`, value);
        setAwaitingCost(value);
    }

    const handleChangeOtherCost = (index: number, value: number, setFieldValue: any) => {
        //setTotal(0);

        setTotalArray(setByIndex(totalArray, value, index-1))
        setFieldValue(`otherCost${index}`, value);
    }

    const handleChangeCurrency = (props: any, value: string) => {
        props.setFieldValue('currency', value);
        setPrefix(value);
    }

    const handleDownload = async (id: string) => {
			await downloadFile(id);
    }

    const handleShowEditImage = (name: string) => {
        setEditableDocs((prevDocs) => {
            if (prevDocs.includes(name)) {
                return prevDocs.filter((doc) => doc !== name);
            }

            return [...prevDocs, name];
        });
    }

    const getListShipName = async () => {
        const response = await getDataVessel() as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedOptions = response.data.map((item: any) => ({ value: item.vesselId, label: item.vesselName }));
            setListShipName(formattedOptions);
        }
    }

    const getDataDetail = async () => {
        const response = await getNominationById(id) as any;
        if (response?.status === HttpStatusCode.Ok) {
            if (response.data.sire || response.data.bire) {
							setDefaultCurrency({ label: 'USD', value: 'USD' });
							setPrefix('USD');
            } else if (response.data.vetting) {
							setDefaultCurrency({ label: 'IDR', value: 'IDR' });
							setPrefix('IDR');
            }

            setDataDetail(response.data);
            setCostInspection(response.data?.costInspection);
            setAwaitingCost(response.data?.awaitingDaysCost);
            setTotal(response.data?.totalAmount);
            setDefaultShipName({ value: response.data?.vesselId, label: response.data?.shipName });
        } else {
            toast({
                title: 'Failed to load invoice data.',
                description: "We unabled to load invoice data for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    useEffect(() => {
        getListShipName();
    }, []);

    useEffect(() => {
        if (id) {
            getDataDetail();
        }
    }, [id])

    useEffect(() => {
        const awaitingFee = awaitingDays * awaitingCost;

        const total = (costInspection ? Number(costInspection) : 0) +
            (awaitingFee ? Number(awaitingFee) : 0) +
            totalArray.reduce((prev, curr) => prev + curr, 0);
        setTotal(total);
    }, [
        costInspection,
        awaitingDays,
        awaitingCost,
        totalArray,
    ]);

    return (
        <>
            <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
            <div className="form-container">
                <Formik
                    initialValues={{
                        invoiceNumber: dataDetail?.invoiceNumber,
                        shipName: defaultShipName?.value,
                        inspectionDate: dataDetail?.inspectionDate,
                        invoiceDate: format(new Date(), FORMAT_ADD_DATE),
                        country: dataDetail?.country,
                        port: dataDetail?.portOfInspection,
                        jobDescription: dataDetail?.jobDescription || '',
                        costInspection: dataDetail?.costInspection === 0 ? null : dataDetail?.costInspection,
                        awaitingDays: dataDetail?.awaitingDays === 0 ? null : dataDetail?.awaitingDays,
                        awaitingCost: null,
                        other1: dataDetail?.other1 || '',
                        otherCost1: dataDetail?.otherCost1 === 0 ? null : dataDetail?.otherCost1,
                        otherCostFile1: dataDetail?.otherEvidence1Id || '',
                        other2: dataDetail?.other2 || '',
                        otherCost2: dataDetail?.otherCost2 === 0 ? null : dataDetail?.otherCost2,
                        otherCostFile2: dataDetail?.otherEvidence2Id || '',
                        other3: dataDetail?.other3 || '',
                        otherCost3: dataDetail?.otherCost3 === 0 ? null : dataDetail?.otherCost3,
                        otherCostFile3: dataDetail?.otherEvidence3Id || '',
                        other4: dataDetail?.other4 || '',
                        otherCost4: dataDetail?.otherCost4 === 0 ? null : dataDetail?.otherCost4,
                        otherCostFile4: dataDetail?.otherEvidence4Id || '',
                        other5: dataDetail?.other5 || '',
                        otherCost5: dataDetail?.otherCost5 === 0 ? null : dataDetail?.otherCost5,
                        otherCostFile5: dataDetail?.otherEvidence5Id || '',
                        other6: dataDetail?.other6 || '',
                        otherCost6: dataDetail?.otherCost6 === 0 ? null : dataDetail?.otherCost6,
                        otherCostFile6: dataDetail?.otherEvidence6Id || '',
                        other7: dataDetail?.other7 || '',
                        otherCost7: dataDetail?.otherCost7 === 0 ? null : dataDetail?.otherCost7,
                        otherCostFile7: dataDetail?.otherEvidence7Id || '',
                        other8: dataDetail?.other8 || '',
                        otherCost8: dataDetail?.otherCost8 === 0 ? null : dataDetail?.otherCost8,
                        otherCostFile8: dataDetail?.otherEvidence8Id || '',
                        other9: dataDetail?.other9 || '',
                        otherCost9: dataDetail?.otherCost9 === 0 ? null : dataDetail?.otherCost9,
                        otherCostFile9: dataDetail?.otherEvidence9Id || '',
                        other10: dataDetail?.other10 || '',
                        otherCost10: dataDetail?.otherCost10 === 0 ? null : dataDetail?.otherCost10,
                        otherCostFile10: dataDetail?.otherEvidence10Id || '',
                        totalAmount: dataDetail?.totalAmount === 0 ? null : dataDetail?.totalAmount,
                        currency: defaultCurrency?.value,
                        files: null,
                        inspectionResult: dataDetail?.inspectionProcessResultId || '',
                        invoiceStatus: dataDetail?.invoiceStatus,
                        paymentStatus: dataDetail?.paymentStatus,
                        note: dataDetail?.note || ''
                    }}
                    validationSchema={() => invoiceInspectorSchema(isDraft)}
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}
                    enableReinitialize={true}
                >
                    {(props) => (
                        <Form>
                            <Field name='invoiceNumber'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.invoiceNumber && form.touched.invoiceNumber)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Invoice Number<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Invoice Number' className="form-input" isDisabled={isSubmitting} />
                                                <FormErrorMessage>{String(form.errors.invoiceNumber)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='shipName'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.shipName && form.touched.shipName)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Vessel Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <SelectShip name='shipName' placeholder="Choose Ship" handleChange={({ value }: any) => props.setFieldValue('shipName', value)} value={props.values.shipName} options={listShipName} hasError={Boolean(form.errors.shipName && form.touched.shipName)} isDisabled />
                                                <FormErrorMessage>{String(form.errors.shipName)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name="inspectionDate">
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.inspectionDate && form.touched.inspectionDate)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Inspection Date<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div style={{
                                                width: '100%',
                                                height: '2.5rem',
                                                fontSize: '1rem',
                                                paddingInlineStart: '1rem',
                                                paddingInlineEnd: '1rem',
                                                borderRadius: '0.375rem',
                                                minWidth: '0px',
                                                outline: '2px solid transparent',
                                                outlineOffset: '2px',
                                                position: 'relative',
                                                appearance: 'none',
                                                transitionProperty: 'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
                                                paddingTop: '0.35rem',
                                                marginBottom: (form.errors.inspectionDate && form.touched.inspectionDate) ? '1.25rem' : 0,
                                                border: form.errors.inspectionDate && form.touched.inspectionDate ? '2px solid red' : '1px solid #e5e7e9'
                                            }}>
                                                <DatePicker
                                                    id="inspectionDate"
                                                    selected={field.value as Date | null}
                                                    onChange={(date: Date | null) => props.setFieldValue('inspectionDate', date)}
                                                    dateFormat="dd-MM-YYYY"
                                                    placeholderText="Inspection Date"
                                                    readOnly={isSubmitting}
                                                />
                                                <FormErrorMessage>{String(form.errors.inspectionDate)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='invoiceDate'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.invoiceDate && form.touched.invoiceDate)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Invoice Date<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div style={{
                                                width: '100%',
                                                height: '2.5rem',
                                                fontSize: '1rem',
                                                paddingInlineStart: '1rem',
                                                paddingInlineEnd: '1rem',
                                                borderRadius: '0.375rem',
                                                minWidth: '0px',
                                                outline: '2px solid transparent',
                                                outlineOffset: '2px',
                                                position: 'relative',
                                                appearance: 'none',
                                                transitionProperty: 'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
                                                paddingTop: '0.35rem',
                                                marginBottom: (form.errors.invoiceDate && form.touched.invoiceDate) ? '1.25rem' : 0,
                                                border: form.errors.invoiceDate && form.touched.invoiceDate ? '2px solid red' : '1px solid #e5e7e9'
                                            }}>
                                                <DatePicker
                                                    id="invoiceDate"
                                                    selected={field.value as Date | null}
                                                    onChange={(date: Date | null) => props.setFieldValue('invoiceDate', date)}
                                                    dateFormat="dd-MM-YYYY"
                                                    placeholderText="Invoice Date"
                                                    readOnly={isSubmitting}
                                                />
                                                <FormErrorMessage>{String(form.errors.invoiceDate)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='country'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.country && form.touched.country)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Country<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <SelectCountry name='country' placeholder="Choose Country" handleChange={({ value }: any) => props.setFieldValue('country', value)} value={props.values.country} hasError={form.errors.country && form.touched.country} isDisabled={isSubmitting} />
                                                <FormErrorMessage>{String(form.errors.country)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='port'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.port && form.touched.port)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Port<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Port' className="form-input" isDisabled={isSubmitting} />
                                                <FormErrorMessage>{String(form.errors.port)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='jobDescription'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.jobDescription && form.touched.jobDescription)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Job Description<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Textarea {...field} placeholder='Job Description' className="form-input" disabled={isSubmitting} />
                                                <FormErrorMessage>{String(form.errors.jobDescription)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='currency'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.currency && form.touched.currency)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel htmlFor='status-switch' className="form-label">
                                                Currency<span style={{ color: 'red' }}>*</span>
                                            </FormLabel>
                                            <div className="form-input-container">
                                                <SelectCurrency
                                                    name='currency'
                                                    placeholder="Choose Currency"
                                                    handleChange={({ value }: any) => handleChangeCurrency(props, value)}
                                                    value={props.values.currency}
                                                    defaultValue={props.values.currency}
                                                    haserror={Boolean(form.errors.currency && form.touched.currency)}
                                                    isDisabled={isSubmitting}
                                                    width="100%"
                                                />
                                                <FormErrorMessage>{String(form.errors.currency)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='costInspection'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.costInspection && form.touched.costInspection)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Cost Inspection<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <CurrencyInput
                                                    {...field}
                                                    placeholder='Input Cost'
                                                    prefix={prefix}
                                                    value={props.values.costInspection}
                                                    error={Boolean(form.errors.costInspection && form.touched.costInspection)}
                                                    onValueChange={({ floatValue = 0 }) => handleChangeCostInspection(floatValue, props.setFieldValue)}
                                                    disabled={isSubmitting}
                                                    width="100%"
                                                />
                                                <FormErrorMessage>{String(form.errors.costInspection)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='awaitingDays'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.awaitingDays && form.touched.awaitingDays)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Awaiting Days</FormLabel>
                                            <div className="form-input-container">
                                                <div>
                                                    <CurrencyInput
                                                        {...field}
                                                        placeholder='Input Days'
                                                        prefix=''
                                                        value={props.values.awaitingDays}
                                                        onValueChange={({ floatValue = 0 }) => handleChangeAwaitingDays(floatValue, props.setFieldValue)}
                                                        disabled={isSubmitting}
                                                        width='10em'
                                                        
                                                    />
                                                    <span style={{ marginLeft: '5px' }}>Days</span>
                                                </div>
                                                
                                                <FormErrorMessage>{String(form.errors.awaitingDays)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='awaitingCost'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.awaitingCost && form.touched.awaitingCost)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label"></FormLabel>
                                            <div className="form-input-container">
                                                <div>
                                                    <CurrencyInput
                                                        {...field}
                                                        placeholder='Input Cost'
                                                        prefix={prefix}
                                                        value={props.values.awaitingCost}
                                                        onValueChange={({ floatValue = 0 }) => handleChangeAwaitingCost(floatValue, props.setFieldValue)}
                                                        disabled={isSubmitting}
                                                        width='10em'
                                                    />
                                                    <span style={{ marginLeft: '5px' }}>/ Day</span>
                                                </div>
                                                <FormErrorMessage>{String(form.errors.awaitingCost)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            {array1to10.map((num /* 1-10 */, index /* 0-9 */) => visibleOthers.includes(num) && <>
                                <Field name={otherArray[index]}>
                                    {({ field, form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors[otherArray[index]] && form.touched[otherArray[index]])} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Others</FormLabel>
                                                <div className="form-input-container">
                                                    <Textarea {...field} placeholder='Description' className="form-input" isDisabled={isSubmitting} />
                                                    <FormErrorMessage>{String(form.errors[otherArray[index]])}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                                <Field name={otherCostArray[index]}>
                                    {({ field, form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors[otherCostArray[index]] && form.touched[otherCostArray[index]])} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label"></FormLabel>
                                                <div className="form-input-container">
                                                    <CurrencyInput
                                                        {...field}
                                                        placeholder='Input Cost'
                                                        prefix={prefix}
                                                        value={props.values[otherCostArray[index]]}
                                                        error={Boolean(form.errors[otherCostArray[index]] && form.touched[otherCostArray[index]])}
                                                        onValueChange={({ floatValue = 0 }) => handleChangeOtherCost(num, floatValue, props.setFieldValue)}
                                                        disabled={isSubmitting}
                                                        width="100%"
                                                    />
                                                    <FormErrorMessage>{String(form.errors[otherCostArray[index]])}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                                <Field name={`otherCostFile${num}`}>
                                    {({ form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors[`otherCostFile${num}`] && form.submitCount)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label"></FormLabel>
                                                <div className="form-input-container">
                                                    <EditFile
                                                        fileId={dataDetail?.[otherEvidenceFileIdArray[index]] ?? undefined}
                                                        handleDrop={(e) => props.setFieldValue(`otherCostFile${num}`, e[0])} //handleDrop(e, 'otherCostFile1', props.setFieldValue)}
                                                        isInvalid={Boolean(form.errors[`otherCostFile${num}`] && form.submitCount)}
                                                        type="supporting-files"
                                                        isDisabled={props.isSubmitting}
                                                        title="Other File"
                                                    />
                                                    <FormErrorMessage>{String(form.errors[`otherCostFile${num}`])}</FormErrorMessage>
                                                    {!visibleOthers.includes(num+1) && <div>
                                                        {(num < 10) && <AddOthersIcon style={{ marginTop: '7px', marginLeft: '7px', cursor: 'pointer', display: 'inline' }} onClick={() => handleAddOthers(num, isSubmitting)} />}
                                                        {(num > 1) && <RemoveOthersIcon style={{ marginTop: '7px', marginLeft: '7px', cursor: 'pointer', display: 'inline' }} onClick={() => handleRemoveOthers(num, isSubmitting)} />}
                                                    </div>}
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                            </>)}
                            <Field name='totalAmount'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.totalAmount && form.touched.totalAmount)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Total Amount<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <CurrencyInput
                                                    {...field}
                                                    placeholder='Total Amount'
                                                    prefix={prefix}
                                                    value={total}
                                                    width="100%"
                                                    disabled
                                                />
                                                <FormErrorMessage>{String(form.errors.totalAmount)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='inspectionResult'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.inspectionResult && form.touched.inspectionResult)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Inspection Result</FormLabel>
                                            <div className="form-input-container">
                                                {props.values.inspectionResult && <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <Box
                                                        borderWidth="1px"
                                                        borderRadius="md"
                                                        padding={4}
                                                        backgroundColor="green.50"
                                                        borderColor="green.200"
                                                        width="100%"
                                                        marginBottom="15px"
                                                    >
                                                        <HStack
                                                            borderWidth="1px"
                                                            borderRadius="md"
                                                            padding={3}
                                                            backgroundColor="white"
                                                            justifyContent="space-between"
                                                            width="100%"
                                                        >
                                                            <HStack>
                                                                <Text>Inspection Result File</Text>
                                                            </HStack>
                                                            <HStack cursor='pointer' onClick={() => handleDownload(dataDetail.inspectionProcessResultId)}>
                                                                <Icon as={FaEye} />
                                                                <Text>Download Document</Text>
                                                            </HStack>
                                                        </HStack>
                                                    </Box>
                                                    {(props.values.inspectionResult && !isSubmitting) && <EditIcon
                                                        style={{ marginLeft: '10px', marginTop: '-20px', cursor: 'pointer' }}
                                                        onClick={() => handleShowEditImage('doc')}
                                                    />}
                                                </div>}

                                                {(!props.values.inspectionResult || editableDocs.includes('doc')) &&
                                                    <div style={{ width: '95%' }}>
                                                        <FileUploader
                                                            onDrop={(e) => props.setFieldValue('inspectionResult', e[0])} //handleDrop(e, 'inspectionResult', props.setFieldValue)}
                                                            isInvalid={Boolean(form.errors.inspectionResult && form.submitCount)}
                                                            type="inspection-result"
                                                            isDisabled={props.isSubmitting}
                                                            isPadding={false}
                                                        />
                                                </div>
                                                    }
                                            </div>
                                        </div>
                                        <FormErrorMessage>{String(form.errors.inspectionResult)}</FormErrorMessage>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='files'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.files && form.submitCount)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Invoice Files<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <FileUploader onDrop={(e) => handleDrop(e, 'files', props.setFieldValue)} isInvalid={Boolean(form.errors.files && form.submitCount)} isDisabled={props.isSubmitting} type="supporting-files" />
                                                <FormErrorMessage>{String(form.errors.files)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>

                            <Divider />
                            <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 10 }}>
                                <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    isLoading={isSubmitting}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    isLoading={isSubmitting}
                                    onClick={() => setIsDraft(true)}
                                    type='submit'
                                >
                                    Save as Draft
                                </Button>
                                <Button
                                    mt={4}
                                    bgColor={COLORS.BUTTON.BACKGROUND}
                                    color={COLORS.BUTTON.FOREGROUND}
                                    isLoading={isSubmitting}
                                    onClick={() => setIsDraft(false)}
                                    type='submit'
                                    rightIcon={<ArrowIcon />}
                                >
                                    Create New Invoice
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    )
}

export default AddInvoicesInspectorProcess;
